'use client';
import React, { useEffect, useState } from 'react';
import { EXAM_PRESETS, EXAM_TOGGLES, type ExamDuration, type ExamPreset } from '@/data/examPresets';
import { smoothScrollToTop } from '@/utils/scroll';
import type { ExamResult } from '../lib/scoring';
import ExamGrid from './ExamGrid';
import ConfigModal from './ConfigModal';
import ExamInterface from './ExamInterface';
import ExamResults from './ExamResults';

export type Screen = 'select' | 'exam' | 'results';

export interface ExamConfig {
  duration: ExamDuration;
  toggles: Record<(typeof EXAM_TOGGLES)[number]['id'], boolean>;
}

function defaultConfig(exam: ExamPreset): ExamConfig {
  return {
    duration: exam.durationMin,
    toggles: EXAM_TOGGLES.reduce(
      (acc, t) => ({ ...acc, [t.id]: t.defaultOn }),
      {} as ExamConfig['toggles']
    ),
  };
}

/**
 * UI-only orchestrator for the government typing-test experience.
 *
 * It swaps between four static views — exam selection, the Test Settings modal,
 * the split-screen exam interface, and the results screen. No timer or scoring
 * logic is wired up yet; screens are navigable with sample data so the flow and
 * visual design can be reviewed.
 */
export default function GovtTypingTestClient() {
  const [screen, setScreen] = useState<Screen>('select');
  const [selectedExam, setSelectedExam] = useState<ExamPreset | null>(null);
  const [config, setConfig] = useState<ExamConfig | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [result, setResult] = useState<ExamResult | null>(null);
  const [examRunId, setExamRunId] = useState(0);

  useEffect(() => {
    smoothScrollToTop();
  }, [screen]);

  const openConfig = (exam: ExamPreset) => {
    setSelectedExam(exam);
    setConfig(defaultConfig(exam));
    setShowConfig(true);
  };

  const startExam = (finalConfig: ExamConfig) => {
    setConfig(finalConfig);
    setShowConfig(false);
    setResult(null);
    setExamRunId((n) => n + 1);
    setScreen('exam');
  };

  const backToSelect = () => {
    setScreen('select');
    setSelectedExam(null);
    setConfig(null);
  };

  return (
    <div className="fade-in">
      {screen === 'select' && <ExamGrid exams={EXAM_PRESETS} onSelect={openConfig} />}

      {screen === 'exam' && selectedExam && config && (
        <ExamInterface
          key={examRunId}
          exam={selectedExam}
          config={config}
          onReconfigure={() => setShowConfig(true)}
          onSubmit={(r) => {
            setResult(r);
            setScreen('results');
          }}
          onExit={backToSelect}
        />
      )}

      {screen === 'results' && selectedExam && config && result && (
        <ExamResults
          exam={selectedExam}
          result={result}
          onTryAgain={() => {
            setExamRunId((n) => n + 1);
            setResult(null);
            setScreen('exam');
          }}
          onNewExam={backToSelect}
        />
      )}

      {showConfig && selectedExam && config && (
        <ConfigModal
          exam={selectedExam}
          config={config}
          onChange={setConfig}
          onClose={() => setShowConfig(false)}
          onStart={startExam}
        />
      )}
    </div>
  );
}
